import { useState } from "react";

import { RotateCw } from "lucide-react";
import { useDispatch } from "react-redux";

import { useRetrySummaryGeneration } from "@/hooks/mutations/use-files";
import { setMessage } from "@/redux/notification";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { Button } from "@components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { ConfirmationModal } from "../../ConfirmationModal";

type RegenerateFileConfirmationProps = {
  file: {
    id: string;
    name: string;
    classification?: string;
    date?: string;
    case?: unknown;
  };
};

export const RegenerateFileConfirmation = ({
  file,
}: RegenerateFileConfirmationProps) => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);

  const retrySummaryGeneration = useRetrySummaryGeneration();

  const handleRegenerate = () => {
    retrySummaryGeneration.mutate(file.id, {
      onSuccess: () => {
        dispatch(
          setMessage({
            content: "Summary regeneration started successfully",
            type: "success",
          })
        );
        setIsOpen(false); // Close the modal on success
      },
      onError: (error) => {
        dispatch(
          setMessage({
            content: error.message || "Failed to regenerate summary",
            type: "error",
          })
        );
      },
    });
  };

  return (
    <>
      <ConfirmationModal
        type="info"
        icon={<RotateCw className="size-14 text-primary" />}
        title="Regenerate Summary?"
        description={`Are you sure you want to regenerate the summary for "${stripBundlePrefix(file.name)}"? This will replace the current summary with a new one.`}
        confirmButtonText="Yes, Regenerate"
        cancelButtonText="Cancel"
        onConfirm={handleRegenerate}
        isLoading={retrySummaryGeneration.isPending}
        open={isOpen}
        onOpenChange={setIsOpen}
      />

      {/* The trigger lives outside ConfirmationModal (rather than as its
          asChild children) so it can sit inside a Tooltip — Tooltip isn't
          Slot-compatible, so nesting it inside the dialog's own asChild slot
          would swallow the click that opens the dialog. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 w-auto p-1"
            onClick={() => setIsOpen(true)}
          >
            <RotateCw className="size-4 text-primary" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Regenerate Summary</TooltipContent>
      </Tooltip>
    </>
  );
};
